/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

export const DOCK_ANCHORS = ['left', 'right', 'top', 'bottom'];

export const DOCK_PICKER_ANCHORS = ['left', 'right', 'top', 'bottom'];

export const DRAWER_MIN_VERTICAL_SIZE_PX = 360;

export const DRAWER_MIN_HORIZONTAL_SIZE_PX = 220;

/** @deprecated Use DRAWER_MIN_VERTICAL_SIZE_PX or DRAWER_MIN_HORIZONTAL_SIZE_PX */
export const DRAWER_MIN_SIZE_PX = DRAWER_MIN_VERTICAL_SIZE_PX;

export const DRAWER_DEFAULT_VERTICAL_SIZE_PX = 620;

export const DRAWER_DEFAULT_HORIZONTAL_SIZE_PX = 280;

export const DEFAULT_DOCK_ANCHOR = 'right';

export const DRAWER_LAYOUT_MODES = ['overlap', 'push'];

export const DEFAULT_LAYOUT_MODE = 'overlap';

export const COMMAND_INTELLIGENCE_CONTENT_SELECTOR = '.main-container > .content';

export const isValidDockAnchor = anchor => DOCK_ANCHORS.includes(anchor);

export const isSelectableDockAnchor = anchor => DOCK_PICKER_ANCHORS.includes(anchor);

export const normalizeDockAnchor = anchor => {
  if (isSelectableDockAnchor(anchor)) {
    return anchor;
  }

  if (isValidDockAnchor(anchor)) {
    return DEFAULT_DOCK_ANCHOR;
  }

  return DEFAULT_DOCK_ANCHOR;
};

export const isValidLayoutMode = mode => DRAWER_LAYOUT_MODES.includes(mode);

export const isVerticalDock = anchor => anchor === 'left' || anchor === 'right';

export const getDefaultSizePx = anchor => {
  if (isVerticalDock(anchor)) {
    return DRAWER_DEFAULT_VERTICAL_SIZE_PX;
  }

  return DRAWER_DEFAULT_HORIZONTAL_SIZE_PX;
};

export const getContentDimensionPx = (anchor, insets) => {
  if (isVerticalDock(anchor)) {
    return insets?.width ?? 0;
  }

  return insets?.height ?? 0;
};

export const getMinMaxSizePx = (anchor, insets) => {
  const contentDimension = getContentDimensionPx(anchor, insets);
  const fallbackDimension =
    typeof window !== 'undefined'
      ? isVerticalDock(anchor)
        ? window.innerWidth
        : window.innerHeight
      : getDefaultSizePx(anchor);

  const basis = contentDimension > 0 ? contentDimension : fallbackDimension;
  const maxPx = Math.floor(basis * 0.92);
  const minPx = isVerticalDock(anchor) ? DRAWER_MIN_VERTICAL_SIZE_PX : DRAWER_MIN_HORIZONTAL_SIZE_PX;

  return { min: minPx, max: Math.max(minPx, maxPx) };
};

export const clampSizePx = (size, anchor, insets) => {
  const { min, max } = getMinMaxSizePx(anchor, insets);
  const parsed = Number(size);

  if (!Number.isFinite(parsed)) {
    return getDefaultSizePx(anchor);
  }

  return Math.min(max, Math.max(min, Math.round(parsed)));
};

export const getResizeSizeFromPointer = (anchor, start, event) => {
  const clientX = event?.clientX ?? 0;
  const clientY = event?.clientY ?? 0;
  const startX = start?.x ?? 0;
  const startY = start?.y ?? 0;
  const startSize = start?.size ?? 0;

  if (anchor === 'right') {
    return startSize + (startX - clientX);
  }

  if (anchor === 'left') {
    return startSize + (clientX - startX);
  }

  if (anchor === 'bottom') {
    return startSize + (startY - clientY);
  }

  if (anchor === 'top') {
    return startSize + (clientY - startY);
  }

  return startSize;
};

export const getViewportRightInsetPx = insets => {
  if (typeof window === 'undefined') {
    return insets?.right ?? 0;
  }

  return window.innerWidth - (insets?.right ?? window.innerWidth);
};

export const getViewportBottomInsetPx = insets => {
  if (typeof window === 'undefined') {
    return insets?.bottom ?? 0;
  }

  return window.innerHeight - (insets?.bottom ?? window.innerHeight);
};

export const getPaperPositionSx = (anchor, insets, sizePx) => {
  const top = insets?.top ?? 0;
  const left = insets?.left ?? 0;
  const contentWidth = insets?.width ?? 0;
  const contentHeight = insets?.height ?? 0;
  const maxVerticalWidth = Math.floor(contentWidth * 0.92);
  const maxHorizontalHeight = Math.floor(contentHeight * 0.92);

  if (isVerticalDock(anchor)) {
    return {
      position: 'fixed',
      top: `${top}px`,
      height: contentHeight > 0 ? `${contentHeight}px` : '100%',
      width: `${sizePx}px`,
      maxWidth: maxVerticalWidth > 0 ? `${maxVerticalWidth}px` : '92vw',
      ...(anchor === 'right'
        ? {
            right: `${getViewportRightInsetPx(insets)}px`,
            left: 'auto',
          }
        : {
            left: `${left}px`,
            right: 'auto',
          }),
    };
  }

  return {
    position: 'fixed',
    left: `${left}px`,
    width: contentWidth > 0 ? `${contentWidth}px` : '100%',
    height: `${sizePx}px`,
    maxHeight: maxHorizontalHeight > 0 ? `${maxHorizontalHeight}px` : '92vh',
    ...(anchor === 'bottom'
      ? {
          bottom: `${getViewportBottomInsetPx(insets)}px`,
          top: 'auto',
        }
      : {
          top: `${top}px`,
          bottom: 'auto',
        }),
  };
};

export const getContentPushPaddingStyle = (anchor, sizePx) => {
  const size = `${sizePx}px`;

  return {
    paddingTop: anchor === 'top' ? size : '',
    paddingRight: anchor === 'right' ? size : '',
    paddingBottom: anchor === 'bottom' ? size : '',
    paddingLeft: anchor === 'left' ? size : '',
  };
};

export const buildCommandIntelligenceDockCacheKey = widthCacheKey => `${widthCacheKey}-dock`;

export const buildCommandIntelligenceLayoutModeCacheKey = widthCacheKey => `${widthCacheKey}-layout-mode`;

export const buildCommandIntelligenceAutoHideCacheKey = widthCacheKey => `${widthCacheKey}-auto-hide-overlap`;

export const buildCommandIntelligenceOpenCacheKey = widthCacheKey => `${widthCacheKey}-open`;

export const parsePersistedAutoHide = raw => raw === 'true' || raw === true;

export const parsePersistedOpen = raw => raw === 'true' || raw === true;

export const buildCommandIntelligenceSizeCacheKey = (widthCacheKey, anchor) => `${widthCacheKey}-size-${anchor}`;
